import { Button, Typography } from 'components';
import { toaster } from 'helpers/toaster';
import { CreditCardIcon, TrashIcon } from 'lib/svgs/Icons';
import { useTranslations } from 'next-intl';
import { useCreditCard } from 'providers/BillingInfoProviders/useCreditCard';
import { useCreditCardDelete } from 'providers/BillingInfoProviders/useCreditCardDelete';
import { FC, useState } from 'react';
import { notifyException } from 'services/appsignal';
import { ESpansException } from 'types/ESpans';
import ModalDeleteCreditCard from './ModalDeleteCreditCard';

interface ICreditCardContent {
  onClick: () => void;
}

const CreditCardContent: FC<ICreditCardContent> = ({ onClick }) => {
  const t = useTranslations();

  const [openDelete, setOpenDelete] = useState(false);

  const handleOpenDelete = () => setOpenDelete(true);
  const handleCloseDelete = () => setOpenDelete(false);

  const { data: creditCardData, refetch } = useCreditCard();

  const { mutateAsync: creditCardDelete } = useCreditCardDelete({
    onSuccess: () => toaster.success(t('messages.success.credit_card_deleting')),
    onError: (error: unknown) => {
      notifyException(error, ESpansException.BillingException);
      toaster.error(t('messages.error.credit_card_deleting'));
    }
  });

  const onDelete = async () => {
    await creditCardDelete({});
    handleCloseDelete();
    refetch();
  };

  const isValidCreditCard =
    creditCardData?.creditCard?.brand &&
    creditCardData?.creditCard?.expMonth &&
    creditCardData?.creditCard?.expYear &&
    creditCardData?.creditCard?.last4;

  return (
    <>
      <ModalDeleteCreditCard open={openDelete} onCancel={handleCloseDelete} onClick={onDelete} />
      <label className="">
        <Typography variant="regular4" className="text-tertiary-100 ">
          {t('labels.card_number')}
        </Typography>
      </label>
      <div className="my-2 rounded bg-tertiary-800 px-4 py-2">
        {isValidCreditCard && (
          <div className="flex justify-between ">
            <div className="flex gap-2">
              <CreditCardIcon />
              <span className="flex gap-1">
                <Typography variant="medium3" className="text-tertiary-100">
                  **** **** ****
                </Typography>
                <Typography variant="medium4" className="text-tertiary-100">
                  {creditCardData?.creditCard?.last4}
                </Typography>
              </span>
            </div>
            <Typography variant="light4" className="text-tertiary-200">
              <span>{`${t('credit_card.expires')} `}</span>
              <span>
                {`${creditCardData?.creditCard?.expMonth}/${creditCardData?.creditCard?.expYear}`}
              </span>
            </Typography>
          </div>
        )}
        {!isValidCreditCard && (
          <span>
            {t('credit_card.not_available', {
              date: `${creditCardData?.creditCard?.expMonth}/${creditCardData?.creditCard?.expYear}`
            })}
          </span>
        )}
      </div>
      <div className="flex justify-between">
        <Button
          label={t('credit_card.change')}
          onClick={onClick}
          size="medium"
          className="!m-0 flex items-center !p-0"
          variant="text"
        />
        <a autoFocus data-tooltip-id="tolq-tooltip-top" data-tooltip-content={t('tooltips.delete')}>
          <Button
            onClick={handleOpenDelete}
            size="medium"
            variant="icon"
            colorIcon="red"
            startAdornment={<TrashIcon className="text-secondary-red" />}
          />
        </a>
      </div>
    </>
  );
};

export default CreditCardContent;
